import { useState, useEffect, useRef } from 'react';
import { useWakeLock } from '@/hooks/useWakeLock';
import { useVoiceGuidance } from '@/hooks/useVoiceGuidance';
import { Slider } from '@/components/ui/slider';
import { StepperInput } from './StepperInput';
import { Play, Pause, Volume2, VolumeX, Mic, MicOff, Home, X, RotateCcw } from 'lucide-react';
import { playMetronomeTick, speakInstruction, initVoices } from '@/utils/audioUtils';
import BreathingSquareCapsules from './BreathingSquareCapsules';
import { EXERCISE_BACKGROUNDS, EXERCISE_BG_FALLBACK_COLOR, CO2_WATERMARK_LOGO } from '@/utils/exerciseBackgrounds';
import AirHungerRatingModal from './AirHungerRatingModal';
import ExerciseInstructionsDrawer from './ExerciseInstructionsDrawer';
import { useLastSessionSettings } from '@/hooks/useLastSessionSettings';
import ExerciseTopLogo from './ExerciseTopLogo';
import InhalationGentleWarning from './InhalationGentleWarning';

const BoxBreathingModal = ({ 
  isOpen, 
  onClose, 
  pattern,
  onSessionComplete 
}) => {
  // Session states
  const [isActive, setIsActive] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [phase, setPhase] = useState('ready');
  useWakeLock(isActive && !isPaused);
  useVoiceGuidance(phase, isActive && !isPaused);
  const [progress, setProgress] = useState(0);
  const [cyclesCompleted, setCyclesCompleted] = useState(0);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [voiceEnabled, setVoiceEnabled] = useState(true);
  const [elapsedTime, setElapsedTime] = useState(0); // Track elapsed time
  const [showAirHungerModal, setShowAirHungerModal] = useState(false);
  const [lastSessionId, setLastSessionId] = useState(null);
  const [pendingClose, setPendingClose] = useState(false);
  const [ratedThisSession, setRatedThisSession] = useState(false);
  
  // Box Breathing modal states
  const [sessionDuration, setSessionDuration] = useState(5); // minutes
  const [remainingTime, setRemainingTime] = useState(300); // seconds (5 min default)
  
  // Box breathing adjustable timings
  const [timings, setTimings] = useState({
    inhale: 5,
    holdIn: 5,
    exhale: 5,
    holdOut: 5
  });
  
  // Refs
  const soundEnabledRef = useRef(true);
  
  // Keep soundEnabledRef in sync
  useEffect(() => {
    soundEnabledRef.current = soundEnabled;
  }, [soundEnabled]);

  // Restore last-used settings (session duration + phase timings) when the
  // patient re-launches this exercise from « Mon programme ».
  useLastSessionSettings(isOpen, pattern?.id || 'box-breathing', (settings) => {
    if (typeof settings.sessionDuration === 'number') {
      setSessionDuration(settings.sessionDuration);
      setRemainingTime(settings.sessionDuration * 60);
    }
    if (settings.timings && typeof settings.timings === 'object') {
      setTimings((prev) => ({ ...prev, ...settings.timings }));
    }
  });
  
  // Initialize voices on mount
  useEffect(() => {
    initVoices();
    const timeout = setTimeout(() => initVoices(), 100);
    return () => clearTimeout(timeout);
  }, []);
  
  // Breathing cycle logic
  useEffect(() => {
    if (!isOpen || !isActive || isPaused) return;

    const phases = [
      { name: 'inhale', duration: timings.inhale },
      { name: 'hold-in', duration: timings.holdIn },
      { name: 'exhale', duration: timings.exhale },
      { name: 'hold-out', duration: timings.holdOut },
    ].filter(p => p.duration > 0);

    let currentPhaseIndex = phases.findIndex(p => p.name === phase);
    if (currentPhaseIndex === -1) currentPhaseIndex = 0;

    const currentPhaseDuration = phases[currentPhaseIndex].duration * 1000;
    const updateInterval = 50;
    let elapsed = 0;

    const interval = setInterval(() => {
      elapsed += updateInterval;
      const newProgress = (elapsed / currentPhaseDuration) * 100;

      if (newProgress >= 100) {
        const nextPhaseIndex = (currentPhaseIndex + 1) % phases.length;
        const nextPhaseName = phases[nextPhaseIndex].name;
        setPhase(nextPhaseName);
        setProgress(0);

        if (nextPhaseIndex === 0) {
          setCyclesCompleted(prev => prev + 1);
        }
      } else {
        setProgress(newProgress);
      }
    }, updateInterval);

    return () => clearInterval(interval);
  }, [isOpen, isActive, isPaused, phase, timings]);

  // Countdown timer
  useEffect(() => {
    if (!isOpen || !isActive || isPaused) return;
    
    const interval = setInterval(() => {
      setElapsedTime(prev => prev + 1);
      setRemainingTime(prev => {
        if (prev <= 1) {
          stopSession(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => clearInterval(interval);
  }, [isOpen, isActive, isPaused]);

  // No effect needed for modal open/close - handled by startSession and handleClose

  // Metronome tick every second
  useEffect(() => {
    if (!isOpen || !isActive || isPaused) return;
    
    const interval = setInterval(() => {
      if (soundEnabledRef.current) {
        playMetronomeTick();
      }
    }, 1000);
    
    return () => clearInterval(interval);
  }, [isOpen, isActive, isPaused]);

  // Voice announcement for phase changes
  useEffect(() => {
    if (!isOpen || !isActive || !voiceEnabled) return;
    
    const phaseTexts = {
      'inhale': 'Inspirez',
      'hold-in': 'Retenez',
      'exhale': 'Expirez',
      'hold-out': 'Retenez'
    };
    
    if (phaseTexts[phase]) {
      speakInstruction(phaseTexts[phase]);
    }
  }, [isOpen, isActive, voiceEnabled, phase]);

  const startSession = () => {
    setRemainingTime(sessionDuration * 60);
    setElapsedTime(0);
    setIsActive(true);
    setIsPaused(false);
    setPhase('inhale');
    setProgress(0);
    setCyclesCompleted(0);
    setRatedThisSession(false);
  };

  const handleTogglePause = async () => {
    const goingToPause = !isPaused;
    setIsPaused(!isPaused);
    if (goingToPause && !ratedThisSession && elapsedTime > 10) {
      setRatedThisSession(true);
      let savedSession = null;
      if (onSessionComplete) {
        savedSession = await onSessionComplete({
          patternId: pattern?.id || 'box-breathing',
          patternName: pattern?.name || 'Respiration Carrée',
          durationSeconds: elapsedTime,
          cyclesCompleted: cyclesCompleted,
          settings: { sessionDuration, timings },
        });
      }
      setLastSessionId(savedSession?.id || null);
      setShowAirHungerModal(true);
    }
  };

  const stopSession = async (saveSession = false) => {
    let savedSession = null;
    if (saveSession && onSessionComplete && elapsedTime > 10) {
      savedSession = await onSessionComplete({
        patternId: pattern?.id || 'box-breathing',
        patternName: pattern?.name || 'Respiration Carrée',
        durationSeconds: elapsedTime,
        cyclesCompleted: cyclesCompleted,
        settings: { sessionDuration, timings },
      });
    }
    
    setIsActive(false);
    setIsPaused(false);
    setPhase('ready');
    setProgress(0);
    setCyclesCompleted(0);
    setElapsedTime(0);
    return savedSession;
  };

  // Reset session (go back to settings without saving)
  const resetSession = async () => {
    const wasLongSession = elapsedTime > 10;
    let savedSession = null;
    if (wasLongSession && !ratedThisSession && onSessionComplete) {
      savedSession = await onSessionComplete({
        patternId: pattern?.id || 'box-breathing',
        patternName: pattern?.name || 'Respiration Carrée',
        durationSeconds: elapsedTime,
        cyclesCompleted: cyclesCompleted,
        settings: { sessionDuration, timings },
      });
    }
    setIsActive(false);
    setIsPaused(false);
    setPhase('ready');
    setProgress(0);
    setCyclesCompleted(0);
    setElapsedTime(0);
    if (wasLongSession && !ratedThisSession) {
      setLastSessionId(savedSession?.id || null);
      setRatedThisSession(true);
      setShowAirHungerModal(true);
    }
  };

  const applyAirHungerRecommendation = (direction) => {
    // For Box Breathing, we adjust all 4 phases equally (+1s or -3s, min 3s, max 30s)
    setTimings((prev) => {
      const delta = direction === 'up' ? 1 : direction === 'down-strong' ? -3 : 0;
      return {
        inhale: Math.max(3, Math.min(30, prev.inhale + delta)),
        holdIn: Math.max(3, Math.min(30, prev.holdIn + delta)),
        exhale: Math.max(3, Math.min(30, prev.exhale + delta)),
        holdOut: Math.max(3, Math.min(30, prev.holdOut + delta)),
      };
    });
  };

  const handleClose = async () => {
    const wasLongSession = elapsedTime > 10;
    if (wasLongSession && !ratedThisSession) {
      const saved = await stopSession(true);
      setLastSessionId(saved?.id || null);
      setPendingClose(true);
      setRatedThisSession(true);
      setShowAirHungerModal(true);
    } else {
      stopSession(false);
      onClose();
    }
  };

  const handleAirHungerClose = () => {
    setShowAirHungerModal(false);
    if (pendingClose) {
      setPendingClose(false);
      onClose();
    }
  };

  if (!isOpen || !pattern) return null;

  return (
    <div 
      className="fixed inset-0 z-[60] flex items-center justify-center overflow-auto"
      style={{ 
        backgroundImage: `url(${EXERCISE_BACKGROUNDS.box})`,
        backgroundColor: EXERCISE_BG_FALLBACK_COLOR,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}
    >
      {/* Soft white veil to match the cabinet homepage rendering */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 z-[1]" style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.55) 0%, rgba(240,247,252,0.55) 55%, rgba(214,235,246,0.55) 100%)' }} />
      {/* Floating Navigation Buttons */}
      <div className="absolute top-4 left-4 z-20">
        <button
          onClick={handleClose}
          className="p-3 bg-slate-900/10 hover:bg-slate-900/20 backdrop-blur-md rounded-full transition-colors shadow-sm"
          title="Retour aux exercices"
        >
          <Home size={20} className="text-slate-700" />
        </button>
      </div>
      
      <div className="absolute top-4 right-4 z-20">
        <button
          onClick={handleClose}
          className="p-3 bg-slate-900/10 hover:bg-slate-900/20 backdrop-blur-md rounded-full transition-colors shadow-sm"
          title="Fermer"
        >
          <X size={20} className="text-slate-700" />
        </button>
      </div>

        <ExerciseTopLogo />

      {/* Main Content */}
      <div className="relative z-10 w-full h-full overflow-y-auto px-4 pt-16 sm:pt-20 pb-8 flex flex-col lg:flex-row lg:items-center lg:justify-center lg:pb-8">
        <div className="flex flex-col items-center my-auto gap-6 lg:grid lg:grid-cols-[1fr_auto_1fr] lg:gap-10 lg:items-center w-full">

          {/* LEFT PANEL — Settings (mobile order: 3) */}
          <div className="w-full max-w-sm order-3 lg:order-none lg:col-start-1 lg:row-start-1 lg:justify-self-end lg:w-80 flex-shrink-0">
            {(!isActive || isPaused) && (
              <div className="bg-white/85 backdrop-blur-md rounded-xl px-4 py-4 shadow-md border border-slate-300 space-y-3">
                <h3 className="font-bold text-slate-800 text-center text-lg">
                  {isPaused ? 'Pause - Réglages' : 'Réglages'}
                </h3>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <div className="text-slate-700 text-base font-semibold">Durée session</div>
                    <StepperInput
                      value={sessionDuration}
                      onChange={(v) => {
                        setSessionDuration(v);
                        if (!isActive || isPaused) setRemainingTime(v * 60);
                      }}
                      min={1}
                      max={30}
                      step={1}
                      unit="min"
                      label="durée session"
                      colorClass="cyan"
                      valueTestId="box-duration-value"
                      incTestId="box-duration-inc"
                      decTestId="box-duration-dec"
                    />
                  </div>

                  <div className="space-y-1">
                    <div className="text-slate-700 text-base font-semibold">Durée phases</div>
                    <StepperInput
                      value={timings.inhale}
                      onChange={(v) => setTimings({ inhale: v, holdIn: v, exhale: v, holdOut: v })}
                      min={3}
                      max={8}
                      step={1}
                      unit="sec"
                      label="durée phase"
                      colorClass="sky"
                      valueTestId="box-phase-value"
                      incTestId="box-phase-inc"
                      decTestId="box-phase-dec"
                    />
                  </div>
                </div>
                <p className="text-xs text-slate-500 text-center">
                  Les 4 phases : {timings.inhale}s chacune
                </p>

                <div className="bg-slate-50 rounded-lg p-2 border border-slate-200">
                  <p className="text-[11px] text-slate-500 text-center">
                    Inspirer → Retenir → Expirer → Retenir
                  </p>
                  <p className="text-sm text-slate-700 text-center font-medium mt-0.5">
                    {timings.inhale}s → {timings.holdIn}s → {timings.exhale}s → {timings.holdOut}s
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* CENTER — Square Capsules (mobile order: 1) */}
          {/* The capsule is wrapped in a `relative` box so the cycles counter
              can float BELOW it in absolute position. This keeps the center
              column's measured height equal to the capsule, so the flex row
              can center the side panels' medians on the capsule's median. */}
          <div className="flex flex-col items-center order-1 lg:order-none lg:col-start-2 lg:row-start-1">
            <div className="relative">
              <InhalationGentleWarning visible={isActive && phase === 'inhale'} />
              <BreathingSquareCapsules
                phase={phase === 'hold-in' ? 'hold' : phase}
                progress={progress}
                phaseDurations={{
                  inhale: timings.inhale,
                  hold: timings.holdIn,
                  exhale: timings.exhale,
                  'hold-out': timings.holdOut
                }}
                colors={{
                  inhale: '#67e8f9',
                  hold: '#6ee7b7',
                  exhale: '#fdba74',
                  'hold-out': '#c4b5fd',
                  ready: '#94a3b8'
                }}
                centerTimer={remainingTime}
              />

              {/* Cycle counter — absolute so it does not inflate the column */}
              <div className="absolute left-1/2 -translate-x-1/2 top-full mt-4 bg-white/85 backdrop-blur rounded-xl px-4 py-2 shadow-md border border-slate-300 whitespace-nowrap">
                <span className="text-slate-700 font-semibold">{cyclesCompleted} cycles</span>
              </div>
            </div>
          </div>

          {/* RIGHT PANEL — Controls (mobile order: 2) */}
          <div className="w-full max-w-xs flex-shrink-0 flex justify-center lg:justify-start order-2 lg:order-none lg:col-start-3 lg:row-start-1 lg:justify-self-start lg:w-auto">
            <div className="flex flex-row lg:flex-col gap-3 sm:gap-4 exercise-controls-mobile-fixed">
              <button
                onClick={() => {
                  if (!isActive) {
                    setRemainingTime(sessionDuration * 60);
                    startSession();
                  } else {
                    handleTogglePause();
                  }
                }}
                className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-white/85 backdrop-blur shadow-lg flex items-center justify-center hover:bg-white transition-colors"
                style={{ border: '3px solid rgba(103,232,249,0.6)' }}
                data-testid="box-play-btn"
              >
                {!isActive || isPaused ? (
                  <Play className="w-7 h-7 sm:w-9 sm:h-9 text-slate-700 ml-1" />
                ) : (
                  <Pause className="w-7 h-7 sm:w-9 sm:h-9 text-slate-700" />
                )}
              </button>

              <button
                onClick={() => setSoundEnabled(!soundEnabled)}
                className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-white/85 backdrop-blur shadow-lg flex items-center justify-center hover:bg-white transition-colors"
                style={{ border: '3px solid rgba(103,232,249,0.6)' }}
                title="Son métronome"
                data-testid="box-sound-btn"
              >
                {soundEnabled ? (
                  <Volume2 className="w-6 h-6 sm:w-7 sm:h-7 text-slate-700" />
                ) : (
                  <VolumeX className="w-6 h-6 sm:w-7 sm:h-7 text-slate-400" />
                )}
              </button>

              <button
                onClick={() => setVoiceEnabled(!voiceEnabled)}
                className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-white/85 backdrop-blur shadow-lg flex items-center justify-center hover:bg-white transition-colors"
                style={{ border: '3px solid rgba(103,232,249,0.6)' }}
                title="Voix"
                data-testid="box-voice-btn"
              >
                {voiceEnabled ? (
                  <Mic className="w-6 h-6 sm:w-7 sm:h-7 text-slate-700" />
                ) : (
                  <MicOff className="w-6 h-6 sm:w-7 sm:h-7 text-slate-400" />
                )}
              </button>

              {isActive && phase !== 'ready' && (
                <button
                  onClick={resetSession}
                  className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-white/85 backdrop-blur shadow-lg flex items-center justify-center hover:bg-white transition-colors"
                  style={{ border: '3px solid rgba(239,68,68,0.55)' }}
                  title="Réinitialiser"
                  data-testid="box-reset-btn"
                >
                  <RotateCcw className="w-6 h-6 sm:w-7 sm:h-7 text-red-500" />
                </button>
              )}
            </div>
          </div>

          {/* FAR-RIGHT PANEL — Benefits (mobile only; desktop moved to the right-side instructions drawer) */}
          {!isActive && (
            <div className="w-full max-w-xs flex-shrink-0 order-4 lg:hidden">
              <div className="bg-white/95 backdrop-blur-md rounded-xl shadow-md border border-slate-300 overflow-hidden" data-testid="exercise-benefits-panel">
                <div className="px-4 py-3 border-b border-slate-200 bg-gradient-to-r from-cyan-50 to-violet-50">
                  <h3 className="font-semibold text-slate-900 text-sm">Pourquoi cet exercice ?</h3>
                  <p className="text-[11px] text-slate-500 mt-0.5">Respiration carrée — équilibre du système nerveux</p>
                </div>

                <div className="px-4 py-3 space-y-3 max-h-[65vh] overflow-y-auto">
                  <section>
                    <h4 className="text-[11px] uppercase tracking-wider font-bold text-cyan-700 mb-1.5">Compétences travaillées</h4>
                    <ul className="text-xs text-slate-700 space-y-1.5 list-disc pl-4">
                      <li>
                        <strong>Gestion du débit respiratoire</strong> — vitesse à laquelle l'air entre et sort du nez. Viser progressivement une respiration <em>silencieuse et douce</em>, où l'on ne sent plus l'air passer, où les narines se dilatent légèrement à l'inspiration et où les muscles inspirateurs se relâchent à l'expiration, sans aucun effort.
                      </li>
                      <li>
                        <strong>Tolérance au CO₂</strong> — par la diminution de la fréquence respiratoire et la pratique des apnées inspiratoires et expiratoires.
                      </li>
                      <li>
                        <strong>Équilibre du Système Nerveux Autonome</strong> — grâce à l'égalité stricte entre les 4 phases du cycle.
                      </li>
                    </ul>
                  </section>

                  <section>
                    <h4 className="text-[11px] uppercase tracking-wider font-bold text-emerald-700 mb-1.5">Objectifs visés</h4>
                    <ul className="text-xs text-slate-700 space-y-1 list-disc pl-4">
                      <li>Respiration nasale silencieuse, douce, sans effort</li>
                      <li>Ralentir la fréquence respiratoire</li>
                      <li>Améliorer la tolérance au CO₂ via les apnées</li>
                      <li>Équilibrer parfaitement inspiration (activation) et expiration (relaxation)</li>
                    </ul>
                  </section>

                  <section>
                    <h4 className="text-[11px] uppercase tracking-wider font-bold text-amber-700 mb-1.5">Effets bénéfiques sur le corps</h4>
                    <ul className="text-xs text-slate-700 space-y-1.5 list-disc pl-4">
                      <li><strong>Variabilité cardiaque améliorée :</strong> le rythme régulier renforce la cohérence cœur–respiration.</li>
                      <li><strong>Résilience au stress :</strong> le SNA mieux équilibré rend le corps plus stable face aux tensions.</li>
                      <li><strong>Plasticité du système nerveux :</strong> pratique régulière = meilleure adaptabilité neurologique.</li>
                      <li><strong>Équilibre sympathique/parasympathique :</strong> phase inspiratoire (stress) et expiratoire (relaxation) parfaitement compensées.</li>
                      <li><strong>Meilleure oxygénation :</strong> ralentissement + tolérance CO₂ = diffusion optimale de l'O₂ dans les tissus.</li>
                    </ul>
                  </section>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Instructions drawer (desktop only) — hosts the detailed benefits so the
          3-column grid above stays perfectly symmetric and the capsule remains
          dead-center on the screen. */}
      <ExerciseInstructionsDrawer testIdPrefix="box">
        <div className="bg-white rounded-xl shadow border border-slate-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-200 bg-gradient-to-r from-cyan-50 to-violet-50">
            <h3 className="font-semibold text-slate-900 text-sm">Pourquoi cet exercice ?</h3>
            <p className="text-[11px] text-slate-500 mt-0.5">Respiration carrée — équilibre du système nerveux</p>
          </div>

          <div className="px-4 py-3 space-y-3 max-h-[70vh] overflow-y-auto">
            <section>
              <h4 className="text-[11px] uppercase tracking-wider font-bold text-cyan-700 mb-1.5">Compétences travaillées</h4>
              <ul className="text-xs text-slate-700 space-y-1.5 list-disc pl-4">
                <li>
                  <strong>Gestion du débit respiratoire</strong> — vitesse à laquelle l'air entre et sort du nez. Viser progressivement une respiration <em>silencieuse et douce</em>, où l'on ne sent plus l'air passer, où les narines se dilatent légèrement à l'inspiration et où les muscles inspirateurs se relâchent à l'expiration, sans aucun effort.
                </li>
                <li>
                  <strong>Tolérance au CO₂</strong> — par la diminution de la fréquence respiratoire et la pratique des apnées inspiratoires et expiratoires.
                </li>
                <li>
                  <strong>Équilibre du Système Nerveux Autonome</strong> — grâce à l'égalité stricte entre les 4 phases du cycle.
                </li>
              </ul>
            </section>

            <section>
              <h4 className="text-[11px] uppercase tracking-wider font-bold text-emerald-700 mb-1.5">Objectifs visés</h4>
              <ul className="text-xs text-slate-700 space-y-1 list-disc pl-4">
                <li>Respiration nasale silencieuse, douce, sans effort</li>
                <li>Ralentir la fréquence respiratoire</li>
                <li>Améliorer la tolérance au CO₂ via les apnées</li>
                <li>Équilibrer parfaitement inspiration (activation) et expiration (relaxation)</li>
              </ul>
            </section>

            <section>
              <h4 className="text-[11px] uppercase tracking-wider font-bold text-amber-700 mb-1.5">Effets bénéfiques sur le corps</h4>
              <ul className="text-xs text-slate-700 space-y-1.5 list-disc pl-4">
                <li><strong>Variabilité cardiaque améliorée :</strong> le rythme régulier renforce la cohérence cœur–respiration.</li>
                <li><strong>Résilience au stress :</strong> le SNA mieux équilibré rend le corps plus stable face aux tensions.</li>
                <li><strong>Plasticité du système nerveux :</strong> pratique régulière = meilleure adaptabilité neurologique.</li>
                <li><strong>Équilibre sympathique/parasympathique :</strong> phase inspiratoire (stress) et expiratoire (relaxation) parfaitement compensées.</li>
                <li><strong>Meilleure oxygénation :</strong> ralentissement + tolérance CO₂ = diffusion optimale de l'O₂ dans les tissus.</li>
              </ul>
            </section>
          </div>
        </div>
      </ExerciseInstructionsDrawer>

      <AirHungerRatingModal
        isOpen={showAirHungerModal}
        onClose={handleAirHungerClose}
        sessionId={lastSessionId}
        onApplyRecommendation={applyAirHungerRecommendation}
      />
    </div>
  );
};

export default BoxBreathingModal;
